// useMessageMenu — everything behind a long press on a message, shared by
// the room timeline and the thread. `open(event, anchor)` lifts the row
// and shows the menu; the returned `element` renders the menu and the
// dialogs its actions lead to (full emoji picker, delete confirmation,
// report). One implementation, so a room and a thread behave the same.

import { useCallback, useEffect, useState, type ReactNode, type RefObject } from "react";

import { ConfirmDialog } from "~/components/ConfirmDialog";
import { mediaSource, triggerBrowserDownload } from "~/components/Timeline";
import { EmojiPicker } from "~/components/EmojiPicker";
import { MessageSheet } from "~/components/mobile/MessageSheet";
import { ReportDialog } from "~/components/ReportDialog";
import { mobileStrings } from "~/components/mobile/strings";
import { useMatrix } from "~/context/MatrixContext";
import { useMessageActions } from "~/hooks/useMessageActions";
import { canReportMessage } from "~/lib/matrix";
import type { RoomSummary, SdkBindings, TimelineEvent } from "~/lib/matrix";
import { messageContentBody } from "~/lib/message-utils";
import { insertQuote, type QuoteComposerHandle } from "~/lib/quote";

interface Target {
  event: TimelineEvent;
  anchor: HTMLElement | null;
}

export function useMessageMenu({
  room,
  quoteComposerRef,
  openEdit,
}: {
  room: Pick<RoomSummary, "id" | "isDirect">;
  quoteComposerRef: RefObject<QuoteComposerHandle | null>;
  /** Start editing your own message; omit where editing is not offered. */
  openEdit?: (eventId: string) => void;
}): { open: (event: TimelineEvent, anchor: HTMLElement | null) => void; element: ReactNode } {
  const { client, userId } = useMatrix();
  const { toggleReaction, deleteMessage, ready } = useMessageActions(room.id);
  const [target, setTarget] = useState<Target | null>(null);
  const [picker, setPicker] = useState<Target | null>(null);
  const [deleting, setDeleting] = useState<TimelineEvent | null>(null);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const [reporting, setReporting] = useState<TimelineEvent | null>(null);

  // undefined while loading; null when it can't be saved from the menu.
  const [attachment, setAttachment] = useState<File | null | undefined>(undefined);

  const open = useCallback((event: TimelineEvent, anchor: HTMLElement | null) => {
    if (!canOpenMessageMenu(event)) return;
    setTarget({ event, anchor });
  }, []);
  const close = useCallback(() => setTarget(null), []);

  // Fetched as the sheet opens, and Save offered once it is ready, so the
  // share sheet opens inside the tap; WebKit rejects `navigator.share` once
  // the tap's activation has expired.
  const targetEvent = target?.event ?? null;
  useEffect(() => {
    setAttachment(undefined);
    if (!client || !targetEvent || !messageMenuCapabilities(targetEvent.content.type).save) return;
    let current = true;
    loadAttachmentFile(client.bindings, targetEvent)
      .then((file) => {
        if (current) setAttachment(file);
      })
      .catch((err: unknown) => {
        console.warn("Loading the attachment failed", err);
        if (current) setAttachment(null);
      });
    return () => {
      current = false;
    };
  }, [client, targetEvent]);

  const event = target?.event ?? null;
  const isOwn = event ? event.sender.id === userId : false;
  const canReport = event ? canReportMessage(event.sender.id, userId, room) : false;
  const actions = event
    ? messageMenuActions({ contentType: event.content.type, isOwn, canReport })
    : { copy: false, save: false, quote: false, edit: false, delete: false, report: false };
  const body = event ? (messageContentBody(event.content) ?? "") : "";

  const element = (
    <>
      {target && event && (
        <MessageSheet
          isOwnMessage={isOwn}
          canReact={ready}
          anchor={target.anchor}
          onReact={(emoji) => void toggleReaction(event.id, emoji, event.reactions ?? [])}
          onMoreReactions={() => setPicker(target)}
          onCopyText={actions.copy ? () => void navigator.clipboard?.writeText(body) : undefined}
          onSave={
            actions.save && attachment !== null
              ? () => {
                  if (attachment) void saveAttachment(attachment);
                }
              : undefined
          }
          saveReady={attachment !== undefined}
          onQuote={
            actions.quote
              ? () =>
                  insertQuote(quoteComposerRef, {
                    roomId: room.id,
                    sender: event.sender.displayName,
                    ts: event.ts,
                    eventId: event.id,
                    body,
                  })
              : undefined
          }
          onEdit={actions.edit && openEdit ? () => openEdit(event.id) : undefined}
          onDelete={() => setDeleting(event)}
          onReport={actions.report ? () => setReporting(event) : undefined}
          onClose={close}
        />
      )}
      {picker && (
        <EmojiPicker
          anchor={picker.anchor}
          onSelect={(emoji) => {
            void toggleReaction(picker.event.id, emoji, picker.event.reactions ?? []);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title={mobileStrings.messageSheet.deleteTitle}
          body={
            deleteFailed
              ? mobileStrings.messageSheet.deleteFailed
              : mobileStrings.messageSheet.deleteBody
          }
          confirmLabel={mobileStrings.messageSheet.delete}
          onConfirm={() => {
            const event = deleting;
            setDeleting(null);
            setDeleteFailed(false);
            deleteMessage(event.id).catch((err: unknown) => {
              console.warn("Deleting the message failed", err);
              setDeleteFailed(true);
              setDeleting(event);
            });
          }}
          onCancel={() => {
            setDeleting(null);
            setDeleteFailed(false);
          }}
        />
      )}
      {reporting && (
        <ReportDialog
          roomId={room.id}
          eventId={reporting.id}
          senderId={reporting.sender.id}
          senderName={reporting.sender.displayName || reporting.sender.id}
          onClose={() => setReporting(null)}
        />
      )}
    </>
  );

  return { open, element };
}

const MEDIA_TYPES = new Set(["m.image", "m.video", "m.audio", "m.file"]);
type MenuCapabilities = {
  openable: boolean;
  copy: boolean;
  save: boolean;
  quote: boolean;
  edit: boolean;
};

export function messageMenuCapabilities(type: string): MenuCapabilities {
  if (type === "m.text")
    return { openable: true, copy: true, save: false, quote: true, edit: true };
  if (type === "m.notice" || type === "m.emote")
    return { openable: true, copy: true, save: false, quote: true, edit: false };
  if (MEDIA_TYPES.has(type))
    return { openable: true, copy: false, save: true, quote: false, edit: false };
  if (type === "m.location")
    return { openable: true, copy: false, save: false, quote: false, edit: false };
  return { openable: false, copy: false, save: false, quote: false, edit: false };
}

type MessageActionSet = {
  copy: boolean;
  save: boolean;
  quote: boolean;
  edit: boolean;
  delete: boolean;
  report: boolean;
};

export function messageMenuActions(args: {
  contentType: string;
  isOwn: boolean;
  canReport: boolean;
}): MessageActionSet {
  const cap = messageMenuCapabilities(args.contentType);
  return {
    copy: cap.copy,
    save: cap.save,
    quote: cap.quote,
    edit: cap.edit && args.isOwn,
    delete: args.isOwn,
    report: args.canReport,
  };
}

export function canOpenMessageMenu(event: TimelineEvent): boolean {
  if (event.redacted) return false;
  if (event.status === "sending") return false;
  return messageMenuCapabilities(event.content.type).openable;
}

export async function loadAttachmentFile(
  bindings: Pick<SdkBindings, "downloadMedia">,
  event: TimelineEvent,
): Promise<File | null> {
  const content = event.content;
  if (
    content.type !== "m.image" &&
    content.type !== "m.video" &&
    content.type !== "m.audio" &&
    content.type !== "m.file"
  ) {
    return null;
  }
  const name = content.body || "attachment";
  const type = content.mimetype ?? "application/octet-stream";
  if (content.url?.startsWith("blob:")) {
    // A message still being sent shows the picked file itself.
    return new File([await (await fetch(content.url)).blob()], name, { type });
  }
  if ((content.size ?? 0) > SAVE_PREFETCH_MAX_BYTES) return null;
  const source = mediaSource(content);
  if (!source) return null;
  const { bytes } = await bindings.downloadMedia(source);
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return new File([buffer], name, { type });
}

const DOWNLOAD_URL_LIFETIME_MS = 60_000;
/** Above this, a long-press (to react, say) shouldn't pull the whole file into memory. */
const SAVE_PREFETCH_MAX_BYTES = 50 * 1024 * 1024;

/** iOS WebViews ignore `download`; the share sheet offers "Save Image" / "Save to Files". */
export async function saveAttachment(file: File): Promise<void> {
  try {
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file] });
      return;
    }
    const url = URL.createObjectURL(file);
    triggerBrowserDownload(url, file.name);
    window.setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_URL_LIFETIME_MS);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") return;
    console.warn("Saving the attachment failed", err);
  }
}
